import {ShareableStaticGraph} from '../../../navigation/SharedGraph';
// src/shared/ui/chart/RankTower.jsx

import React, { useMemo } from "react";

/**
 * RankTower
 * - Renders a ranked list of items for a single "snapshot" (i.e., one year).
 * - Each item is a horizontal bar whose width is proportional to its numeric value.
 * - Designed to answer: "Where does X land compared to everyone else?"
 *
 * This is intentionally NOT Chart.js:
 * - Faster to render for 150–200 rows
 * - Much easier to make the "tower" look clean
 *
 * @template T
 *
 * @typedef {Object} RankTowerProps
 * @property {T[]} rows
 *
 * @property {string[]} yearOptions
 * @property {string} selectedYearKey
 * @property {(nextYearKey: string)=>void} onYearChange
 *
 * @property {string} [title="Rank Tower"]
 * @property {string} [subtitle] - Optional line under the title.
 *
 * @property {keyof T | ((row:T)=>string)} [labelAccessor="districtName"]
 * @property {keyof T | ((row:T)=>number|null)} [valueAccessor="value"]
 *
 * @property {string} [highlightLabel="GLASTONBURY"]
 * @property {(label:string)=>string} [normalizeLabel] - Used for highlight matching.
 *
 * @property {"desc"|"asc"} [sortOrder="desc"]
 * @property {boolean} [includeNulls=true] - If false, rows with null/NaN values are removed.
 *
 * @property {(value:number|null, row?:T)=>string} [formatValue] - Right-side value display.
 * @property {(value:number)=>string} [barAriaLabel] - Optional aria label for bars.
 *
 * @property {boolean} [useScroll=false] - If true, constrain height and allow vertical scroll.
 * @property {number} [maxHeightPx=520] - Only used when useScroll=true.
 *
 * @property {string} [highlightRowClassName="bg-yellow-100"]
 * @property {string} [highlightBarClassName="bg-blue-600"]
 * @property {string} [defaultBarClassName="bg-slate-500"]
 *
 * @property {boolean} [showHighlightSummary=true] - Show "Rank X / N (top Y%) • value" for highlight.
 */

/** @param {any} v */
function isFiniteNumber(v) {
  return typeof v === "number" && Number.isFinite(v);
}

/** @param {any} s */
function defaultNormalizeLabel(s) {
  return String(s ?? "")
    .trim()
    .toUpperCase();
}

/** @param {any} v */
function defaultFormatValue(v) {
  if (!isFiniteNumber(v)) return "—";
  return Number(v).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });
}

/**
 * @template T
 * @param {T} row
 * @param {keyof T | ((row:T)=>any)} accessor
 */
function readAccessor(row, accessor) {
  if (typeof accessor === "function") return accessor(row);
  if (accessor && typeof accessor === "string") return row?.[accessor];
  return undefined;
}

/**
 * Generic Rank Tower component.
 * @template T
 * @param {RankTowerProps<T>} props
 * @returns {JSX.Element}
 */
export function RankTower(props) {
  return <ShareableStaticGraph title={props.title || 'Rank tower'}><RankTowerContent {...props}/></ShareableStaticGraph>;
}
function RankTowerContent({
  rows,
  yearOptions,
  selectedYearKey,
  onYearChange,

  title = "Rank Tower",
  subtitle,

  labelAccessor = "districtName",
  valueAccessor = "value",

  highlightLabel = "GLASTONBURY",
  normalizeLabel = defaultNormalizeLabel,

  sortOrder = "desc",
  includeNulls = true,

  formatValue = defaultFormatValue,
  barAriaLabel,

  useScroll = false,
  maxHeightPx = 520,

  highlightRowClassName = "bg-yellow-100",
  highlightBarClassName = "bg-blue-600",
  defaultBarClassName = "bg-slate-500",

  showHighlightSummary = true,
}) {
  const safeRows = Array.isArray(rows) ? rows : [];

  const normalizedHighlight = normalizeLabel(highlightLabel);

  const ranked = useMemo(() => {
    const cleaned = safeRows
      .map((r) => {
        const labelRaw = readAccessor(r, labelAccessor);
        const valueRaw = readAccessor(r, valueAccessor);

        const label = String(labelRaw ?? "").trim();
        const value = isFiniteNumber(valueRaw) ? valueRaw : null;

        return { _row: r, label, value };
      })
      .filter((x) => normalizeLabel(x.label) !== "");

    const list = includeNulls
      ? cleaned
      : cleaned.filter((x) => isFiniteNumber(x.value));

    // Sort by value then by label
    list.sort((a, b) => {
      const av = a.value;
      const bv = b.value;

      const aOk = isFiniteNumber(av);
      const bOk = isFiniteNumber(bv);

      // Nulls always bottom
      if (aOk && !bOk) return -1;
      if (!aOk && bOk) return 1;

      if (aOk && bOk) {
        if (av !== bv) {
          return sortOrder === "asc" ? av - bv : bv - av;
        }
      }

      return String(a.label).localeCompare(String(b.label));
    });

    return list;
  }, [safeRows, labelAccessor, valueAccessor, includeNulls, normalizeLabel, sortOrder]);

  const maxValue = useMemo(() => {
    let m = null;
    for (const r of ranked) {
      if (isFiniteNumber(r.value)) {
        if (m == null || r.value > m) m = r.value;
      }
    }
    return m;
  }, [ranked]);

  const highlightRankInfo = useMemo(() => {
    if (!showHighlightSummary) return null;

    const idx = ranked.findIndex((r) => normalizeLabel(r.label) === normalizedHighlight);
    if (idx === -1) return null;

    const target = ranked[idx];
    const n = ranked.length;

    // "top X%" heuristic: top row => 100%, bottom row => 0% (ish)
    const rank = idx + 1; // 1 = best/highest if sortOrder is desc; still a "position" either way
    const pctTop = n > 1 ? 1 - (rank - 1) / (n - 1) : 1;

    return {
      label: target.label,
      value: target.value,
      rank,
      total: n,
      pctTop,
    };
  }, [ranked, normalizeLabel, normalizedHighlight, showHighlightSummary]);

  return (
    <div className="mt-3 border border-gray-300 rounded bg-white">
      {/* Header */}
      <div className="px-4 pt-2 pb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          {title ? <h3 className="text-lg font-semibold">{title}</h3> : null}

          {subtitle ? (
            <div className="mt-0.5 text-xs text-gray-600">{subtitle}</div>
          ) : null}

          {highlightRankInfo ? (
            <div className="mt-0.5 text-xs text-gray-600">
              <span className="font-semibold text-gray-800">{highlightRankInfo.label}</span>
              {" — "}
              Rank{" "}
              <span className="font-semibold text-gray-800">
                {highlightRankInfo.rank}/{highlightRankInfo.total}
              </span>
              {isFiniteNumber(highlightRankInfo.pctTop) ? (
                <>
                  {" "}
                  (top{" "}
                  <span className="font-semibold text-gray-800">
                    {Math.round(highlightRankInfo.pctTop * 100)}%
                  </span>
                  )
                </>
              ) : null}
              {" "}
              •{" "}
              <span className="font-semibold text-gray-800">
                {formatValue(highlightRankInfo.value, highlightRankInfo)}
              </span>
            </div>
          ) : showHighlightSummary ? (
            <div className="mt-0.5 text-xs text-gray-500">
              Highlight item not found in this year.
            </div>
          ) : null}
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-2 text-xs shrink-0">
          <span className="text-gray-600">Year:</span>
          <select
            value={selectedYearKey || ""}
            onChange={(e) => onYearChange?.(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 bg-white text-xs"
          >
            {(yearOptions || []).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 pb-4">
        {!ranked.length ? (
          <div className="text-sm text-gray-500 py-6">No rows to display.</div>
        ) : (
<div
  className="border border-gray-200 rounded"
  style={
    useScroll
      ? { maxHeight: `${maxHeightPx}px`, overflowY: "auto" }
      : undefined
  }
>
            {ranked.map((r, idx) => {
              const isHighlight = normalizeLabel(r.label) === normalizedHighlight;

              const widthPct =
                isFiniteNumber(r.value) && isFiniteNumber(maxValue) && maxValue > 0
                  ? Math.max(0, Math.min(100, (r.value / maxValue) * 100))
                  : 0;

              return (
                <div
                  key={`${r.label}__${idx}`}
                  className={`flex items-center gap-3 px-2 py-1 border-b border-gray-100 ${
                    isHighlight ? highlightRowClassName : ""
                  }`}
                  title={`${r.label} — ${formatValue(r.value, r._row)}`}
                >
                  <div className="w-[260px] min-w-[260px] text-xs text-gray-800 flex items-center gap-2">
                    <span className="w-[44px] text-right tabular-nums text-gray-500">
                      #{idx + 1}
                    </span>
                    <span className="truncate">{r.label}</span>
                  </div>

                  <div className="flex-1 min-w-[220px]">
                    <div className="h-3 bg-gray-100 rounded" aria-hidden="true">
                      <div
                        className={`h-3 rounded ${
                          isHighlight ? highlightBarClassName : defaultBarClassName
                        }`}
                        style={{ width: `${widthPct}%` }}
                        aria-label={
                          typeof barAriaLabel === "function"
                            ? barAriaLabel(r.value ?? 0)
                            : undefined
                        }
                      />
                    </div>
                  </div>

                  <div className="w-[140px] min-w-[140px] text-xs text-right tabular-nums text-gray-700">
                    {formatValue(r.value, r._row)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}

/**
 * Compatibility wrapper (old name) so existing call sites keep working.
 * If you want, you can migrate call sites to <RankTower /> later.
 *
 * Expected row shape: { districtName, ncep }
 */
export function NcepRankTower(props) {
  return (
    <RankTower
      {...props}
      title={props?.title ?? "NCEP – District Rank (Tower)"}
      labelAccessor={props?.labelAccessor ?? "districtName"}
      valueAccessor={props?.valueAccessor ?? "ncep"}
      formatValue={
        props?.formatValue ??
        ((v) => {
          if (!isFiniteNumber(v)) return "—";
          return `$${Number(v).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        })
      }
    />
  );
}

export default NcepRankTower;
